import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, FileText, Grid3x3, Beaker, Dna, TestTube, Clock, CheckCircle, AlertCircle, Zap, X } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, startOfWeek, endOfWeek, addMonths, subMonths } from 'date-fns';
import { listJobs, listOdysseyJobs, getJobsForMonth, getOdysseyJobsForMonth } from '../../../features/jobs/lib/jobs';
import { listEntries, listDataEntries, getEntriesForMonth, getDataEntriesForMonth } from '../../../features/notebook/lib/entries';
import { listProjects, getProjectsForMonth } from '../../../features/projects/lib/projects';
import type { JobWithResult } from '../../../features/jobs/lib/jobs';
import type { Entry, DataEntry, Project } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';

interface CalendarEvent {
  id: string;
  title: string;
  type: 'job' | 'entry' | 'data_entry' | 'project' | 'odyssey_job';
  date: Date;
  status?: string;
  workflowType?: string;
  projectId?: string;
  projectName?: string;
}

export function Calendar() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showModal, setShowModal] = useState(false);
  
  // Month-based caching
  const [loadedMonths, setLoadedMonths] = useState<Set<string>>(new Set());
  const [monthlyEvents, setMonthlyEvents] = useState<Map<string, CalendarEvent[]>>(new Map());

  const getMonthKey = (year: number, month: number) => `${year}-${month}`;

  const loadEventsForMonth = useCallback(async (year: number, month: number) => {
    const monthKey = getMonthKey(year, month);
    
    // Check if month is already loaded
    if (loadedMonths.has(monthKey)) {
      const cachedEvents = monthlyEvents.get(monthKey) || [];
      setEvents(cachedEvents);
      return;
    }

    try {
      setLoading(true);
      
      const results = await Promise.allSettled([
        getJobsForMonth(year, month),
        getEntriesForMonth(year, month), 
        getDataEntriesForMonth(year, month),
        getProjectsForMonth(year, month),
        getOdysseyJobsForMonth(year, month)
      ]);

      // Extract successful results with error handling
      const [jobsResult, entriesResult, dataEntriesResult, projectsResult, odysseyJobsResult] = results;
      
      const jobs = jobsResult.status === 'fulfilled' ? jobsResult.value : [];
      const entries = entriesResult.status === 'fulfilled' ? entriesResult.value : [];
      const dataEntries = dataEntriesResult.status === 'fulfilled' ? dataEntriesResult.value : [];
      const projects = projectsResult.status === 'fulfilled' ? projectsResult.value : [];
      const odysseyJobs = odysseyJobsResult.status === 'fulfilled' ? odysseyJobsResult.value : [];

      // Log any errors
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const names = ['jobs', 'entries', 'dataEntries', 'projects', 'odysseyJobs'];
          console.error(`Error loading ${names[index]} for ${monthKey}:`, result.reason);
        }
      });

      const monthEvents: CalendarEvent[] = [];

      // Add jobs to events
      jobs.forEach((job) => {
        monthEvents.push({
          id: job.id,
          title: job.name,
          type: 'job',
          date: new Date(job.created_at),
          status: job.status,
          workflowType: job.workflow_type,
          projectId: job.project_id || undefined,
          projectName: projects.find(p => p.id === job.project_id)?.name
        });
      });

      // Add entries to events
      entries.forEach((entry) => {
        monthEvents.push({
          id: entry.id,
          title: entry.name,
          type: 'entry',
          date: new Date(entry.created_at),
          projectId: entry.project_id,
          projectName: projects.find(p => p.id === entry.project_id)?.name
        });
      });

      // Add data entries to events
      dataEntries.forEach((dataEntry) => {
        monthEvents.push({
          id: dataEntry.id,
          title: dataEntry.name,
          type: 'data_entry',
          date: new Date(dataEntry.created_at),
          projectId: dataEntry.project_id,
          projectName: projects.find(p => p.id === dataEntry.project_id)?.name
        });
      });

      // Add projects to events
      projects.forEach((project) => {
        monthEvents.push({
          id: project.id,
          title: project.name,
          type: 'project',
          date: new Date(project.created_at),
          workflowType: project.workflow_type
        });
      });

      // Add odyssey jobs to events
      odysseyJobs.forEach((odysseyJob) => {
        monthEvents.push({
          id: odysseyJob.job_id,
          title: odysseyJob.name || 'Odyssey Job',
          type: 'odyssey_job',
          date: new Date(odysseyJob.created_at),
          status: odysseyJob.status,
          projectId: odysseyJob.project_id || undefined,
          projectName: projects.find(p => p.id === odysseyJob.project_id)?.name
        });
      });

      // Cache the results
      setLoadedMonths(prev => new Set([...prev, monthKey]));
      setMonthlyEvents(prev => new Map([...prev, [monthKey, monthEvents]]));
      setEvents(monthEvents);
      
    } catch (error) {
      console.error('Error loading calendar events for month:', error);
    } finally {
      setLoading(false);
    }
  }, [loadedMonths, monthlyEvents]);

  useEffect(() => {
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    loadEventsForMonth(currentYear, currentMonth);
  }, [currentDate, loadEventsForMonth]);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const nextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const prevMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const goToToday = () => setCurrentDate(new Date());

  const getEventsForDate = (date: Date) => {
    return events.filter(event => isSameDay(event.date, date));
  };

  const getEventIcon = (event: CalendarEvent) => {
    switch (event.type) {
      case 'job':
        if (event.workflowType === 'enzyme') return <Beaker className="h-3 w-3" />;
        if (event.workflowType === 'antibody') return <Dna className="h-3 w-3" />;
        if (event.workflowType === 'peptide') return <TestTube className="h-3 w-3" />;
        return <FileText className="h-3 w-3" />;
      case 'entry':
        return <FileText className="h-3 w-3" />;
      case 'data_entry':
        return <Grid3x3 className="h-3 w-3" />;
      case 'project':
        return <CalendarIcon className="h-3 w-3" />;
      case 'odyssey_job':
        return <Zap className="h-3 w-3" />;
      default:
        return <FileText className="h-3 w-3" />;
    }
  };

  const getEventColor = (event: CalendarEvent) => {
    switch (event.type) {
      case 'job':
        if (event.status === 'completed') return 'bg-green-100 text-green-700 hover:bg-green-200';
        if (event.status === 'failed') return 'bg-red-100 text-red-700 hover:bg-red-200';
        if (event.status === 'running') return 'bg-blue-100 text-blue-700 hover:bg-blue-200';
        return 'bg-gray-100 text-gray-700 hover:bg-gray-200';
      case 'entry':
        return 'bg-amber-100 text-amber-700 hover:bg-amber-200';
      case 'data_entry':
        return 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200';
      case 'project':
        return 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200';
      case 'odyssey_job':
        if (event.status === 'completed') return 'bg-purple-100 text-purple-700 hover:bg-purple-200';
        if (event.status === 'failed') return 'bg-red-100 text-red-700 hover:bg-red-200';
        if (event.status === 'running' || event.status === 'processing') return 'bg-purple-50 text-purple-600 hover:bg-purple-100';
        return 'bg-purple-50 text-purple-600 hover:bg-purple-100';
      default:
        return 'bg-gray-100 text-gray-700 hover:bg-gray-200';
    }
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-3 w-3 text-green-600" />;
      case 'running':
        return <Clock className="h-3 w-3 text-blue-600 animate-spin" />;
      case 'failed':
        return <AlertCircle className="h-3 w-3 text-red-600" />;
      default:
        return <Clock className="h-3 w-3 text-gray-400" />;
    }
  };

  const handleEventClick = (event: CalendarEvent) => {
    switch (event.type) {
      case 'job':
        if (event.projectId) {
          navigate(`/projects/${event.projectId}/jobs/${event.id}`);
        }
        break;
      case 'entry':
        if (event.projectId) {
          navigate(`/projects/${event.projectId}/entries/${event.id}`);
        }
        break;
      case 'data_entry':
        if (event.projectId) {
          navigate(`/projects/${event.projectId}/data-entries/${event.id}`);
        }
        break;
      case 'project':
        navigate(`/projects/${event.id}`);
        break;
      case 'odyssey_job':
        if (event.projectId) {
          navigate(`/projects/${event.projectId}/odyssey/${event.id}`);
        }
        break;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-sm border border-gray-200">
      {/* Calendar Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {format(currentDate, 'MMMM yyyy')}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={prevMonth}
              className="p-2 hover:bg-gray-100 rounded-sm transition-colors"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-5 w-5 text-gray-600" />
            </button>
            <button
              onClick={nextMonth}
              className="p-2 hover:bg-gray-100 rounded-sm transition-colors"
              aria-label="Next month"
            >
              <ChevronRight className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="p-4">
        {/* Day headers */}
        <div className="grid grid-cols-7 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((day) => {
            const dayEvents = getEventsForDate(day);
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isSelectedDay = selectedDate && isSameDay(day, selectedDate);
            const isTodayDate = isToday(day);

            return (
              <div
                key={day.toISOString()}
                className={`min-h-[100px] p-2 border rounded-sm transition-colors cursor-pointer ${
                  isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                } ${
                  isSelectedDay ? 'border-gray-700 bg-gray-100' : 'border-gray-200'
                } ${
                  isTodayDate ? 'ring-2 ring-gray-500' : ''
                } hover:bg-gray-50`}
                onClick={() => {
                  setSelectedDate(day);
                  setShowModal(true);
                }}
              >
                <div className={`text-sm font-medium mb-1 ${
                  isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                } ${isTodayDate ? 'text-gray-800' : ''}`}>
                  {format(day, 'd')}
                </div>
                
                {/* Events */}
                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map((event) => (
                    <div
                      key={event.id}
                      className={`text-xs px-1.5 py-0.5 rounded flex items-center gap-1 truncate ${getEventColor(event)}`}
                      title={`${event.title}${event.projectName ? ` - ${event.projectName}` : ''}`}
                    >
                      {getEventIcon(event)}
                      {(event.type === 'job' || event.type === 'odyssey_job') && getStatusIcon(event.status)}
                      <span className="truncate">{event.title}</span>
                    </div>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-xs text-gray-500 pl-1">
                      +{dayEvents.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal for Selected Date Events */}
      {showModal && selectedDate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-sm max-w-md w-full mx-4 max-h-96 flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-900">
                Events on {format(selectedDate, 'MMMM d, yyyy')}
              </h3>
              <button 
                onClick={() => setShowModal(false)}
                className="p-1 hover:bg-gray-100 rounded-sm transition-colors"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {(() => {
                const dayEvents = getEventsForDate(selectedDate);
                const groupedEvents = {
                  project: dayEvents.filter(e => e.type === 'project'),
                  job: dayEvents.filter(e => e.type === 'job'),
                  odyssey_job: dayEvents.filter(e => e.type === 'odyssey_job'),
                  entry: dayEvents.filter(e => e.type === 'entry'),
                  data_entry: dayEvents.filter(e => e.type === 'data_entry')
                };
                
                if (dayEvents.length === 0) {
                  return <p className="text-sm text-gray-500 text-center py-8">No events on this date</p>;
                }
                
                return (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    <div className="space-y-3">
                        {groupedEvents.project.length > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-700 mb-1">
                              New projects created:
                            </h4>
                            <div className="space-y-1">
                              {groupedEvents.project.map((event) => (
                                <div
                                  key={event.id}
                                  onClick={() => handleEventClick(event)}
                                  className={`p-3 rounded-sm cursor-pointer transition-colors ${getEventColor(event)}`}
                                >
                                  <div className="flex items-center gap-2">
                                    {getEventIcon(event)}
                                    <span className="font-medium text-sm">{event.title}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {groupedEvents.job.length > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-700 mb-1">
                              New jobs created:
                            </h4>
                            <div className="space-y-1">
                              {groupedEvents.job.map((event) => (
                                <div
                                  key={event.id}
                                  onClick={() => handleEventClick(event)}
                                  className={`p-3 rounded-sm cursor-pointer transition-colors ${getEventColor(event)}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      {getEventIcon(event)}
                                      <span className="font-medium text-sm">{event.title}</span>
                                      {getStatusIcon(event.status)}
                                    </div>
                                  </div>
                                  {event.projectName && (
                                    <div className="text-xs mt-1 opacity-75">
                                      Project: {event.projectName}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {groupedEvents.odyssey_job.length > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-700 mb-1">
                              New generations ran:
                            </h4>
                            <div className="space-y-1">
                              {groupedEvents.odyssey_job.map((event) => (
                                <div
                                  key={event.id}
                                  onClick={() => handleEventClick(event)}
                                  className={`p-3 rounded-sm cursor-pointer transition-colors ${getEventColor(event)}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      {getEventIcon(event)}
                                      <span className="font-medium text-sm">{event.title}</span>
                                      {getStatusIcon(event.status)}
                                    </div>
                                  </div>
                                  {event.projectName && (
                                    <div className="text-xs mt-1 opacity-75">
                                      Project: {event.projectName}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {groupedEvents.entry.length > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-700 mb-1">
                              New entries created:
                            </h4>
                            <div className="space-y-1">
                              {groupedEvents.entry.map((event) => (
                                <div
                                  key={event.id}
                                  onClick={() => handleEventClick(event)}
                                  className={`p-3 rounded-sm cursor-pointer transition-colors ${getEventColor(event)}`}
                                >
                                  <div className="flex items-center gap-2">
                                    {getEventIcon(event)}
                                    <span className="font-medium text-sm">{event.title}</span>
                                  </div>
                                  {event.projectName && (
                                    <div className="text-xs mt-1 opacity-75">
                                      Project: {event.projectName}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {groupedEvents.data_entry.length > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-700 mb-1">
                              New data tables created:
                            </h4>
                            <div className="space-y-1">
                              {groupedEvents.data_entry.map((event) => (
                                <div
                                  key={event.id}
                                  onClick={() => handleEventClick(event)}
                                  className={`p-3 rounded-sm cursor-pointer transition-colors ${getEventColor(event)}`}
                                >
                                  <div className="flex items-center gap-2">
                                    {getEventIcon(event)}
                                    <span className="font-medium text-sm">{event.title}</span>
                                  </div>
                                  {event.projectName && (
                                    <div className="text-xs mt-1 opacity-75">
                                      Project: {event.projectName}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}